import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../../context/AppContext";
import useHttp from "../../../hooks/useHttp";

export interface WorkflowEnumItem {
    code: string;
    idleLabel: string;
    activeLabel: string;
    order: number;
    meta: {
        group?: string;
        kind?: string;
        description?: string;
        config?: Record<string, unknown>;
        /** Trigger codes (e.g. "Trigger.Message.Inbound", "Trigger.Message.Outbound") this condition/action supports. If absent, supports all triggers. */
        supportedTriggers?: string[];
    };
}

export interface WorkflowEnumOption {
    value: string;
    label: string;
    desc?: string;
}

export interface WorkflowEnumsData {
    triggerTypes: WorkflowEnumItem[];
    actionTypes: WorkflowEnumItem[];
    conditionTypes: WorkflowEnumItem[];
    scheduleOptions?: WorkflowEnumOption[];
}

export interface UseWorkflowEnumsResult {
    conditionTypes: WorkflowEnumItem[];
    actionTypes: WorkflowEnumItem[];
    triggerTypes: WorkflowEnumItem[];
    scheduleOptions: WorkflowEnumOption[];
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

const DEFAULT_ENUMS: WorkflowEnumsData = {
    triggerTypes: [],
    actionTypes: [],
    conditionTypes: [],
    scheduleOptions: []
};

export function useWorkflowEnums(): UseWorkflowEnumsResult {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const [data, setData] = useState<WorkflowEnumsData>(DEFAULT_ENUMS);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const loadEnums = useCallback(async () => {
        if (!appProperties) return;
        setIsLoading(true);
        setError(null);
        try {
            const rawResponse = await fetchData("workflows/enums", "GET", null, appProperties);
            if (rawResponse == null || rawResponse === "") {
                setData(DEFAULT_ENUMS);
                return;
            }
            let parsedResponse: unknown;
            try {
                parsedResponse = typeof rawResponse === "string" ? JSON.parse(rawResponse) : rawResponse;
            } catch {
                setError(new Error("Invalid JSON response from workflows/enums"));
                setData(DEFAULT_ENUMS);
                return;
            }
            const responseData = parsedResponse && typeof parsedResponse === "object" ? (parsedResponse as Record<string, unknown>) : null;
            const apiBody = responseData?.data ?? responseData;
            const apiPayload = apiBody && typeof apiBody === "object" ? apiBody as Record<string, unknown> : null;
            const payload = apiPayload ? (apiPayload.data ?? apiPayload) : null;
            const payloadObj = payload && typeof payload === "object" && !Array.isArray(payload) ? payload as Record<string, unknown> : null;
            if (payloadObj) {
                const conditionTypes = Array.isArray(payloadObj.conditionTypes) ? payloadObj.conditionTypes : [];
                const runScheduleConditionItem = conditionTypes.find(
                    (conditionItem: WorkflowEnumItem) => conditionItem.code === "Condition.Time.RunSchedule"
                );
                const scheduleConfigFromApi = runScheduleConditionItem?.meta?.config?.schedule as
                    | Array<{ code?: string; idleLabel?: string; meta?: { description?: string } }>
                    | undefined;
                const scheduleOptionsFromApi: WorkflowEnumOption[] = Array.isArray(scheduleConfigFromApi)
                    ? scheduleConfigFromApi.map((scheduleItem) => {
                          const scheduleCode = scheduleItem.code ?? "";
                          const scheduleValue = scheduleCode === "Custom" ? "SpecificHours" : scheduleCode;
                          const scheduleLabel =
                              scheduleCode === "Custom"
                                  ? "Specific Hours"
                                  : (scheduleItem.idleLabel ?? scheduleItem.code ?? "");
                          return { value: scheduleValue, label: scheduleLabel, desc: scheduleItem.meta?.description };
                      })
                    : [];

                const actionTypes = Array.isArray(payloadObj.actionTypes) ? payloadObj.actionTypes : [];

                const conditionTypesWithNumberGroup = conditionTypes.map((conditionItem: WorkflowEnumItem) => {
                    if (conditionItem.code === "Condition.Message.NumberMatch") {
                        return { ...conditionItem, meta: { ...conditionItem.meta, group: "Number" } };
                    }
                    return conditionItem;
                });

                setData({
                    triggerTypes: Array.isArray(payloadObj.triggerTypes) ? payloadObj.triggerTypes : [],
                    actionTypes,
                    conditionTypes: conditionTypesWithNumberGroup,
                    scheduleOptions: scheduleOptionsFromApi.length > 0 ? scheduleOptionsFromApi : undefined
                });
            } else {
                setData(DEFAULT_ENUMS);
            }
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setData(DEFAULT_ENUMS);
        } finally {
            setIsLoading(false);
        }
    }, [appProperties, fetchData]);

    useEffect(() => {
        loadEnums();
    }, [loadEnums]);

    const scheduleOptions =
        data.scheduleOptions && data.scheduleOptions.length > 0
            ? data.scheduleOptions
            : [
                  { value: "24_7", label: "24/7", desc: "Always active" },
                  { value: "BusinessHours", label: "Business Hours", desc: "Mon-Fri, 9am-5pm" },
                  { value: "OutsideBusinessHours", label: "Outside Business Hours", desc: "Evenings & weekends" },
                  { value: "SpecificHours", label: "Specific Hours", desc: "Custom time range" }
              ];

    return {
        conditionTypes: data.conditionTypes,
        actionTypes: data.actionTypes,
        triggerTypes: data.triggerTypes,
        scheduleOptions,
        isLoading,
        error,
        refetch: loadEnums
    };
}
